import { ACTIVITY_DATE_ID, BREAK_LINE_ID, CHECKBOX_GROUP_ID, CHECKBOX_ID, DATE_ID, HEADER_ID, MULTI_LINE_TEXT_ID, NOTES_ID, NUMBER_ID, RADIO_BUTTON_ID, SELECT_LIST_ID, TEXT_ID, TIME_SPENT_ID } from "../../constants/fieldTypes";
import Checkbox from "../UI/Checkbox";
import CheckboxGroup from "../UI/CheckboxGroup";
import FileUpload from "../UI/FileUpload";
import Input from "../UI/Input";
import RadioButton from "../UI/RadioButton";
import Select from "../UI/Select";

interface StructuredEditFieldProps {
    id: string;
    name: string;
    label: string; 
    fieldTypeId: number; 
    required: boolean;
    value: string;
    disabled: boolean;
    options?: { label: string; value: string; order: number }[];
    mask?: string;
    onChange: (value: string) => void;
    columnClass: string;
}

const StructuredEditField = (props: StructuredEditFieldProps) => {

    const handleOnChange = (value: string) => {
        props.onChange(value);
    }
    
    const getField = (): { element: JSX.Element, requiresLineBreak: boolean } | null => {
        switch (props.fieldTypeId) {
            case CHECKBOX_GROUP_ID:
                return {
                    element: (
                        <CheckboxGroup id={props.id}
                            checkedOptions={JSON.parse(props.value || '[]')}
                            options={props.options || []}
                            label={props.label}
                            disabled={props.disabled}
                            onChange={(v) => handleOnChange(JSON.stringify(v || []))}
                            required={props.required} />
                    ),
                    requiresLineBreak: true
                };
            case MULTI_LINE_TEXT_ID:
                return {
                    element: (<div><label>{props.label}</label>
                        <div>
                            <Input type="multi"
                                id={props.id}
                                value={props.value}
                                disabled={props.disabled}
                                onChange={(e) => handleOnChange(e)}
                                required={props.required} />
                        </div>
                    </div>),
                    requiresLineBreak: true
                };
            case DATE_ID:
                return {
                    element: <Input id={props.id}
                        disabled={props.disabled}
                        type="date"
                        value={props.value}
                        required={props.required}
                        label={props.label}
                        onChange={(e) => handleOnChange(e)} />,
                    requiresLineBreak: false
                };
            case HEADER_ID:
                return { element: <h3 id={props.id}>{props.label}</h3>, requiresLineBreak: true };
            case TEXT_ID:
                return {
                    element: <Input id={props.id}
                        disabled={props.disabled}
                        value={props.value}
                        required={props.required}
                        label={props.label}
                        mask={props.mask}
                        useMaskedValue={props.mask !== undefined}
                        onChange={(e) => handleOnChange(e)} />,
                    requiresLineBreak: false
                };
            case CHECKBOX_ID:
                return {
                    element: <Checkbox id={props.id}
                        disabled={props.disabled}
                        required={props.required}
                        checked={props.value?.toString()?.toLowerCase() === "true"}
                        label={props.label}
                        onChange={(checked) => handleOnChange(checked.toString().toLowerCase())} />,
                    requiresLineBreak: false
                };
            case NUMBER_ID:
            case TIME_SPENT_ID:
                return {
                    element: <Input id={props.id}
                        disabled={props.disabled}
                        required={props.required}
                        value={props.value}
                        mask="PositiveDecimalNumber"
                        label={props.label}
                        type="number" 
                        onChange={(e) => handleOnChange(e)} />,
                    requiresLineBreak: false
                };
            case BREAK_LINE_ID:
                return { element: <></>, requiresLineBreak: true }; 
            case RADIO_BUTTON_ID:
                return {
                    element: <RadioButton id={props.id}
                        disabled={props.disabled} required={props.required}
                        value={JSON.parse(props.value || '{}')?.value || ''}
                        label={props.label}
                        onChange={(v) => handleOnChange(JSON.stringify((v && { label: props.options?.find((o: any) => o.value === v)?.label, value: v }) || {}))}
                        options={props.options || []} />, requiresLineBreak: false
                };
            case SELECT_LIST_ID:
                return {
                    element: (
                        <Select id={props.id} 
                            required={props.required}
                            disabled={props.disabled} 
                            value={JSON.parse(props.value || '{}')?.value || ''} 
                            label={props.label}
                            onChange={(e) => handleOnChange(JSON.stringify((e && { label: props.options?.find((o: any) => o.value === e)?.label, value: e }) || {}))} >
                            <option>--Select--</option>
                            {props.options?.map((o: any) => {
                                return <option key={o.value} value={o.value}>{o.label}</option>
                            })}
                        </Select>
                    ),
                    requiresLineBreak: false
                }
        }

        return null;
    }

    const field = getField();
    if (!field) {
        return null;
    }

    if (field.requiresLineBreak) {
        return (<div className="column-12 margin-top-6" key={props.id}>
            {field.element}
        </div>)
    }

    return (<div className={`${props.columnClass} margin-top-6`} key={props.id}>
        {field.element}
    </div>);
};

export default StructuredEditField;